import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { projectLabel, type Project } from '@/services/projectService'

interface SelectProductModalProps {}

export interface SelectProductModalRef {
  open: (project: Project) => void
  close: () => void
}

const SelectProductModal = forwardRef<SelectProductModalRef, SelectProductModalProps>((props, ref) => {
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [project, setProject] = useState<Project | null>(null)

  useImperativeHandle(ref, () => ({
    open: (projectData: Project) => {
      setProject(projectData)
      setIsOpen(true)
    },
    close: () => {
      setIsOpen(false)
      setProject(null)
    },
  }))

  const handleSelectProduct = (productPath: string) => {
    if (!project) return

    navigate(`/projects/${project.id}/products/${productPath}`)
    setIsOpen(false)
    setProject(null)
  }

  const handleClose = () => {
    setIsOpen(false)
    setProject(null)
  }

  if (!isOpen || !project) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('selectProductModal.title', 'Select Product')}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {t('selectProductModal.subtitle', 'Choose a product to navigate for')} <span className="font-medium text-gray-700">{projectLabel(project)}</span>
              </p>
            </div>
            <button
              onClick={handleClose}
              className="ml-4 text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Products List */}
        <div className="p-6">
          <div className="space-y-3">
            {/* Pipeline Studio */}
            <button
              onClick={() => handleSelectProduct('pipeline-studio')}
              className="w-full flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all group"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mr-4 shrink-0">
                <img
                  src="/PS.png"
                  alt="Pipeline Studio"
                  className="w-12 h-12 rounded-lg"
                />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  Pipeline Studio
                </div>
                <div className="text-sm text-gray-500 mt-0.5">
                  {t('selectProductModal.pipelineStudioDesc', 'Data pipeline management and orchestration')}
                </div>
              </div>
              <svg
                className="w-5 h-5 text-gray-400 group-hover:text-primary transition-colors shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50 flex items-center justify-end">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-all font-medium"
          >
            {t('selectProductModal.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  )
})

SelectProductModal.displayName = 'SelectProductModal'

export default SelectProductModal
